import { CloseCircleFilled, ExclamationCircleOutlined } from '@ant-design/icons';
import posthog from '@leyan/lytics';
import { LocalMessage, getFailCauseMsg } from 'hooks/business/conversation';
import type { DialogueRefObject } from 'components/Dialogue/types';
import { css } from '@emotion/css';
import { eventBus } from 'services/eventBus';
import { AuthBeAboutToExpireFeature } from 'utils/featureFlag';
import { AuthFailMsg } from '../components/AuthInvalid/AuthFailMsg';
import ErrorReason from '../components/ErrorReason';
import { UIMessages } from '../useChatView';
import { failCauseCode } from '../constants';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ShopeeChatDiversion } from './MessageExtend/ShopeeChatDiversion';

export const MessageStatusDecorate = ({
  message,
  status,
  content,
  failCause,
}: {
  message: LocalMessage;
  status?: UIMessages['status'];
  content: React.ReactNode;
  failCause?: Conversation.FailContent;
  dialogRef: DialogueRefObject | null;
}) => {
  const sysLang = useSystemLanguageValue();

  if (message.localStatus === 'uploading') {
    return (
      <div className="relative max-w-[100%]" key="uploading">
        <div
          className={messageIcon}
          onClick={() => {
            eventBus.emit('CANCEL_SEND_MESSAGE', message);
            posthog.capture('$cancelSendMessage', { message });
          }}
        >
          <CloseCircleFilled />
        </div>
        {content}
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="relative max-w-[100%]" key="loading">
        {content}
      </div>
    );
  }

  const msg = getFailCauseMsg(sysLang, failCause);

  if (status === 'error') {
    let failCauseNode: React.ReactNode = null;
    if (failCauseCode.includes(failCause?.code) && AuthBeAboutToExpireFeature) {
      failCauseNode = <AuthFailMsg message={message} />;
    } else if (failCause?.code === 'shop_bound_subaccount') {
      failCauseNode = <ShopeeChatDiversion message={message} />;
    } else {
      failCauseNode = (
        <ErrorReason msg={msg} message={message} reSendImage={failCause?.reSendImage} />
      );
    }
    return (
      <>
        <div className="relative max-w-[100%]">
          <div className={messageIcon}>
            <ExclamationCircleOutlined className="!text-sm" />
          </div>
          {content}
        </div>
        {failCauseNode}
      </>
    );
  }

  return (
    <div className="relative max-w-[100%]">
      <div className={messageIcon} />
      {content}
    </div>
  );
};

const messageIcon = css`
  position: absolute;
  top: 50%;
  left: -8px;
  transform: translateX(-100%) translateY(-50%);
  .anticon-close-circle {
    cursor: pointer;
    color: var(--ant-error-color);
  }
  .anticon-exclamation-circle {
    color: var(--ant-error-color);
  }
`;
